import axios, { AxiosResponse } from "axios";
import {
  LOGIN_URL,
  LOGOUT_URL,
  REFRESH_TOKEN_URL,
  VERIFY_2FA_URL,
  VERIFY_RECOVERY_CODE_URL,
  SELECT_BRANCH_URL,
} from "../../api/constants/authRoutes";
import { LoginCredentials, LoginResponse } from "../../types/auth/auth.types";

/**
 * Inicia sesión con credenciales y retorna los tokens y accesos de menú.
 * @param {LoginCredentials} credentials - Correo y contraseña del usuario.
 * @returns {Promise<AxiosResponse<LoginResponse>>} Respuesta con tokens y configuración de acceso.
 */
export const login = async ({
  email,
  password,
}: LoginCredentials): Promise<AxiosResponse<LoginResponse>> => {
  return axios.post<LoginResponse>(LOGIN_URL, { email, password });
};

/**
 * Verifica el código de autenticación en dos pasos (2FA).
 * @param {string} email - Correo electrónico del usuario.
 * @param {string} code - Código generado por la app de autenticación.
 * @returns {Promise<AxiosResponse<any>>} Resultado de verificación.
 */
export const verify2FA = async (email: string, code: string) =>
  axios.post(VERIFY_2FA_URL, { email, code });

/**
 * Verifica un código de recuperación en lugar del 2FA.
 * @param {string} email - Correo electrónico del usuario.
 * @param {string} code - Código de recuperación.
 * @returns {Promise<AxiosResponse<any>>} Resultado de verificación.
 */
export const verifyRecoveryCode = async (email: string, code: string) =>
  axios.post(VERIFY_RECOVERY_CODE_URL, { email, code });

/**
 * Selecciona con qué sucursal entrar (solo aplica a usuarios con más de 1
 * sucursal asignada) y recibe los tokens finales.
 * @param {string} email - Correo electrónico del usuario.
 * @param {number} branchId - Sucursal elegida.
 * @returns {Promise<AxiosResponse<any>>} Resultado con los tokens.
 */
export const selectBranch = async (email: string, branchId: number) =>
  axios.post(SELECT_BRANCH_URL, { email, branchId });

/**
 * Cierra la sesión del usuario invalidando el token.
 * @returns {Promise<boolean>} `true` si el logout fue exitoso, `false` si falló.
 */
export const logout = async (): Promise<boolean> => {
  try {
    const accessToken = JSON.parse(sessionStorage.getItem("auth") || "{}")?.accessToken;
    if (!accessToken) return false;

    await axios.post(
      LOGOUT_URL,
      {},
      {
        headers: { Authorization: `Bearer ${accessToken}` },
      }
    );
    return true;
  } catch (error) {
    console.error("Error al cerrar sesión:", error);
    return false;
  }
};

/**
 * Solicita un nuevo token de acceso usando el refresh token.
 * @param {string} refreshToken - Token de actualización previamente obtenido.
 * @returns {Promise<any>} Nueva respuesta con token o error.
 */
export const refreshToken = async (refreshToken: string): Promise<any> => {
  try {
    const response = await axios.post(REFRESH_TOKEN_URL, { refreshToken });
    return response;
  } catch (error) {
    console.error("Error al renovar token:", error);
    return { error: "Ocurrió un error al renovar el token." };
  }
};
